import { useState, useMemo, useEffect } from 'react';
import { REGION_OPTIONS } from '../../../constants/raiseQueryPage';

/**
 * Hook to manage page state for RaiseQuery page
 * @returns {Object} Page state and handlers
 */
export const useRaiseQueryPageState = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [queries, setQueries] = useState([]);
  const [allQueries, setAllQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    assignedTo: '',
    projectName: '',
    queryType: '',
  });
  const [currentUserRole, setCurrentUserRole] = useState(() => {
    if (typeof window === 'undefined') return '';
    try {
      const storedUser = JSON.parse(localStorage.getItem('user'));
      return storedUser?.role || '';
    } catch (error) {
      // console.error('Failed to parse user role from localStorage', error);
      return '';
    }
  });
  const [currentUserRegion, setCurrentUserRegion] = useState(() => {
    if (typeof window === 'undefined') return '';
    try {
      const storedUser = JSON.parse(localStorage.getItem('user'));
      return storedUser?.region || '';
    } catch (error) {
      // console.error('Failed to parse user region from localStorage', error);
      return '';
    }
  });
  const [selectedRegion, setSelectedRegion] = useState(() => {
    if (typeof window === 'undefined') return REGION_OPTIONS[0]?.value || '';
    try {
      const storedUser = JSON.parse(localStorage.getItem('user'));
      const role = (storedUser?.role || '').toString().trim().toUpperCase();
      const region = storedUser?.region || '';

      // For ED and GM, use their region if available
      if ((role === 'ED' || role === 'GM') && region) {
        return region;
      }
    } catch (error) {
      // console.error('Failed to parse user from localStorage', error);
    }

    // Otherwise, use first available region
    return REGION_OPTIONS[0]?.value || '';
  });
  const [projectOptions, setProjectOptions] = useState([]);
  const [assignedFilterOptions, setAssignedFilterOptions] = useState([]);
  const [queryTypeOptions, setQueryTypeOptions] = useState([]);
  const [statusSummary, setStatusSummary] = useState({
    submitted: 0,
    open: 0,
    delegated: 0,
    escalated: 0,
    closed: 0,
    needClarification: 0,
  });
  const [regionStatusSummary, setRegionStatusSummary] = useState({});
  const [backendCounts, setBackendCounts] = useState(null);
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [expandedRegions, setExpandedRegions] = useState(new Set());
  const [highlightedRegion, setHighlightedRegion] = useState(null);
  const [selectedRegionCard, setSelectedRegionCard] = useState(null);
  const [selectedStatusCard, setSelectedStatusCard] = useState(null);
  const [tableAssignedFilter, setTableAssignedFilter] = useState('');
  const [edUsersByRegion, setEdUsersByRegion] = useState({});

  // Get filtered region options based on role
  const visibleRegionOptions = useMemo(() => {
    const normalizedRole = (currentUserRole || '').toString().trim().toUpperCase();

    // CH (Chairperson) and MP (Member of Parliament) see all regions
    if (normalizedRole === 'CH' || normalizedRole === 'MP' || normalizedRole === 'CHAIRPERSON' || normalizedRole === 'CHAIRMAN') {
      return REGION_OPTIONS;
    }

    // ED (Executive Director) and GM (General Manager) see only their region
    if (normalizedRole === 'ED' || normalizedRole === 'GM') {
      if (currentUserRegion) {
        const userRegionOption = REGION_OPTIONS.find(
          (option) => option.value === currentUserRegion
        );
        return userRegionOption ? [userRegionOption] : REGION_OPTIONS;
      }
      return REGION_OPTIONS;
    }

    // Default: show all regions
    return REGION_OPTIONS;
  }, [currentUserRole, currentUserRegion]);

  // Update selectedRegion when visible regions change (e.g., role-based filtering)
  useEffect(() => {
    const normalizedRole = (currentUserRole || '').toString().trim().toUpperCase();

    // For ED and GM, ensure selectedRegion matches their region
    if ((normalizedRole === 'ED' || normalizedRole === 'GM') && currentUserRegion) {
      if (selectedRegion !== currentUserRegion) {
        setSelectedRegion(currentUserRegion);
      }
    } else if (visibleRegionOptions.length > 0) {
      // For other roles, ensure selectedRegion is valid
      const isValidRegion = visibleRegionOptions.some(
        (option) => option.value === selectedRegion
      );
      if (!isValidRegion && visibleRegionOptions[0]) {
        setSelectedRegion(visibleRegionOptions[0].value);
      }
    }
  }, [currentUserRole, currentUserRegion, visibleRegionOptions, selectedRegion]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const toggleProject = (projectName) => {
    setExpandedProjects((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(projectName)) {
        newSet.delete(projectName);
      } else {
        newSet.add(projectName);
      }
      return newSet;
    });
  };

  const toggleRegion = (regionCode) => {
    const isCurrentlyExpanded = expandedRegions.has(regionCode);

    // Always set as highlighted when clicked
    setHighlightedRegion(regionCode);

    if (isCurrentlyExpanded) {
      // Collapsing region - collapse all projects within this region first
      setExpandedProjects((prevProjects) => {
        const newProjectsSet = new Set();
        // Remove all projects that belong to this region
        Array.from(prevProjects).forEach((projectKey) => {
          if (!projectKey.startsWith(`${regionCode}-`)) {
            newProjectsSet.add(projectKey);
          }
        });
        return newProjectsSet;
      });

      // Then collapse the region
      setExpandedRegions((prev) => {
        const newSet = new Set(prev);
        newSet.delete(regionCode);
        return newSet;
      });
    } else {
      // Expanding region - keep existing project states
      setExpandedRegions((prev) => {
        const newSet = new Set(prev);
        newSet.add(regionCode);
        return newSet;
      });
    }
  };

  // Reset to page 0 when search or filters change
  useEffect(() => {
    setPage(0);
  }, [searchQuery, filters]);

  return {
    // State
    page,
    rowsPerPage,
    queries,
    allQueries,
    loading,
    total,
    searchQuery,
    filters,
    currentUserRole,
    currentUserRegion,
    selectedRegion,
    projectOptions,
    assignedFilterOptions,
    queryTypeOptions,
    statusSummary,
    regionStatusSummary,
    backendCounts,
    expandedProjects,
    expandedRegions,
    highlightedRegion,
    selectedRegionCard,
    selectedStatusCard,
    tableAssignedFilter,
    edUsersByRegion,
    visibleRegionOptions,
    // Setters
    setPage,
    setRowsPerPage,
    setQueries,
    setAllQueries,
    setLoading,
    setTotal,
    setSearchQuery,
    setFilters,
    setCurrentUserRole,
    setCurrentUserRegion,
    setSelectedRegion,
    setProjectOptions,
    setAssignedFilterOptions,
    setQueryTypeOptions,
    setStatusSummary,
    setRegionStatusSummary,
    setBackendCounts,
    setExpandedProjects,
    setExpandedRegions,
    setHighlightedRegion,
    setSelectedRegionCard,
    setSelectedStatusCard,
    setTableAssignedFilter,
    setEdUsersByRegion,
    // Handlers
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearchChange,
    handleFilterChange,
    toggleProject,
    toggleRegion,
  };
};
